import type { Goal } from "@bioflow/contracts";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMe, useUpdateProfile } from "../api/hooks";
import { NeedTelegram } from "../components/NeedTelegram";
import { BodyFields, EMPTY_BODY, GoalPicker, parseBody, type BodyValues } from "../components/ProfileFields";
import { profile as text } from "../content/site";
import { hapticNotify, hasTelegramContext } from "../lib/telegram";

/**
 * «Изменить данные» с главной и «Редактировать» из профиля: цель и параметры
 * на одном экране, сохраняются одним запросом. Те же поля, что в онбординге.
 */
export function ProfileEditScreen() {
  const navigate = useNavigate();
  const me = useMe();
  const update = useUpdateProfile();
  const [goal, setGoal] = useState<Goal | null>(null);
  const [body, setBody] = useState<BodyValues>(EMPTY_BODY);
  const [error, setError] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    const profile = me.data?.profile;
    if (!profile || seeded) return;
    setSeeded(true);
    setGoal(profile.goal);
    setBody({
      sex: profile.sex,
      heightCm: profile.heightCm?.toString() ?? "",
      weightKg: profile.weightKg?.toString() ?? "",
    });
  }, [me.data?.profile, seeded]);

  if (!hasTelegramContext()) return <NeedTelegram what="Профиль" />;

  const parsed = parseBody(body);
  // Рост и вес — либо оба заполнены, либо ни одного: BMI без одного из них не посчитать.
  const bodyComplete = parsed.heightCm !== null && parsed.weightKg !== null;
  const bodyEmpty = body.heightCm === "" && body.weightKg === "";
  const canSave = (bodyComplete || bodyEmpty) && !update.isPending;

  const save = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        ...(goal ? { goal } : {}),
        ...(body.sex ? { sex: body.sex } : {}),
        ...(bodyComplete ? { heightCm: parsed.heightCm!, weightKg: parsed.weightKg! } : {}),
      });
      hapticNotify("success");
      navigate(-1);
    } catch {
      hapticNotify("error");
      setError("Не удалось сохранить. Попробуйте ещё раз.");
    }
  };

  return (
    <div className="screen screen--plain">
      <h1 className="screen__title">{text.editTitle}</h1>
      <p className="screen__subtitle">{text.editSubtitle}</p>

      <div className="section">{text.goal}</div>
      <GoalPicker value={goal} onChange={setGoal} />

      <div className="section">Параметры</div>
      <BodyFields values={body} onChange={(patch) => setBody((current) => ({ ...current, ...patch }))} />

      {error && <div className="alert" role="alert">{error}</div>}

      <button type="button" className="button" style={{ marginTop: 8 }} onClick={() => void save()} disabled={!canSave}>
        {update.isPending ? "Сохраняем…" : text.save}
      </button>
    </div>
  );
}
